/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactNode, useMemo, useState } from 'react';

import {
  BarChartOutlined,
  HistoryOutlined,
  LineChartOutlined,
  OrderedListOutlined,
  PartitionOutlined,
  ReloadOutlined,
  ShopOutlined,
  ShoppingOutlined,
  SwapOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ModernTabPanel } from '@fastlessjs/backoffice-layer/dist/components/atoms/Tabs/ModernTabPanel';
import { ModernTabs } from '@fastlessjs/backoffice-layer/dist/components/atoms/Tabs/ModernTabs';
import { FeaturePermission } from '@fastlessjs/backoffice-layer/dist/components/organisms/featurePermission';
import { PageProps } from '@fastlessjs/backoffice-layer/dist/components/pages/types';
import { Listing } from '@fastlessjs/backoffice-layer/dist/components/templates/Listing';
import { useEnterprisesByOrg } from '@fastlessjs/backoffice-layer/dist/hooks/stores/useEnterprises';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { Alert, Button, Col, Divider, Empty, Row, Space, Typography } from 'antd';

import { EnterpriseBarChart } from './components/EnterpriseBarChart';
import { EurekaStatusDonut } from './components/EurekaStatusDonut';
import { KpiCard } from './components/KpiCard';
import { OrgSelector } from './components/OrgSelector';
import { PeriodSelector } from './components/PeriodSelector';
import { RecentActivity } from './components/RecentActivity';
import { RoleBarChart } from './components/RoleBarChart';
import { TopProductsTable } from './components/TopProductsTable';
import { TrendAreaChart } from './components/TrendAreaChart';
import { useDashboardOrders } from './hooks/useDashboardOrders';
import type { DashboardListOptions } from './hooks/useDashboardOrders';
import { useDashboardReturns } from './hooks/useDashboardReturns';
import type { DashboardPeriod } from './hooks/useDashboardStats';
import { useDashboardStats } from './hooks/useDashboardStats';
import { createDashboardOrderSchema, createDashboardSuiviSchema } from './schemas';
import type { EnterpriseResolver } from './schemas';
import './Dashboard.less';

debug.registerComponent('DashboardAnalytics');

const SliceError = ({ error, label }: { error: unknown; label: string }) =>
  error ? (
    <Alert
      type="error"
      showIcon
      className="dashboard-analytics-page__slice-error"
      message={`Could not load ${label}`}
      description={
        error instanceof Error
          ? error.message
          : 'Unknown error — confirm ADMIN rights and tenant access.'
      }
    />
  ) : null;

const SliceBody = ({
  error,
  label,
  children,
}: {
  error: unknown;
  label: string;
  children: ReactNode;
}) => (
  <>
    <SliceError error={error} label={label} />
    {children}
  </>
);

const DashboardInner = ({ org: _org }: PageProps) => {
  const [targetOrgId, setTargetOrgId] = useState<string>('');
  const [period, setPeriod] = useState<DashboardPeriod>('week');

  const {
    overview,
    timeline,
    breakdown,
    products,
    loading,
    errors,
    isFetchingAny,
    refetch: refetchStats,
  } = useDashboardStats({ targetOrgId, period });

  // Listing options passed to both the Listing component and the hooks.
  const listingOpts = useMemo<DashboardListOptions>(
    () => ({ tenantId: targetOrgId, period }),
    [targetOrgId, period],
  );

  const ordersQuery = useDashboardOrders(listingOpts);
  const returnsQuery = useDashboardReturns(listingOpts);

  const { data: enterprisesData } = useEnterprisesByOrg(
    targetOrgId,
    Boolean(targetOrgId),
  );

  const resolveEnterprise = useMemo<EnterpriseResolver>(() => {
    const map = new Map<number, string>();
    (enterprisesData ?? []).forEach((e) => {
      map.set(Number(e.enterpriseId), e.enterpriseName);
    });
    return (id?: number | null) => {
      if (id == null) return '—';
      const name = map.get(id);
      return name ? `${name} (#${id})` : `#${id}`;
    };
  }, [enterprisesData]);

  // paginationWithSelect transforms each page to page?.data (items array directly).
  // We take the first loaded page for "Recent activity" — the Listing tabs page through all of them.
  const recentOrders = useMemo(() => {
    const p = ordersQuery.data?.pages?.[0];
    return Array.isArray(p) ? p : [];
  }, [ordersQuery.data]);
  const recentReturns = useMemo(() => {
    const p = returnsQuery.data?.pages?.[0];
    return Array.isArray(p) ? p : [];
  }, [returnsQuery.data]);

  // Toolbar refresh: stats + listing infinite queries (recent activity + tabs).
  const isFetchingAnyAll =
    isFetchingAny || ordersQuery.isFetching || returnsQuery.isFetching;

  const refetch = () =>
    Promise.all([refetchStats(), ordersQuery.refetch(), returnsQuery.refetch()]);

  const periodHint = useMemo(() => overview?.period?.label ?? '', [overview]);

  const toolbar = (
    <Space wrap size="middle">
      <OrgSelector value={targetOrgId} onChange={setTargetOrgId} />
      <PeriodSelector value={period} onChange={setPeriod} />
      <Button
        icon={<ReloadOutlined />}
        onClick={() => refetch()}
        loading={isFetchingAnyAll}
      >
        Refresh
      </Button>
    </Space>
  );

  const tabItems = useMemo(
    () => [
      {
        key: 'overview',
        label: (
          <span>
            <LineChartOutlined /> Overview
          </span>
        ),
        children: (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={14}>
              <ModernTabPanel
                title="Daily trends"
                description="Orders and returns per day over the selected window."
                icon={<LineChartOutlined />}
                iconVariant="blue"
                variant="subtle"
              >
                <SliceBody error={errors.timeline} label="daily trends">
                  <TrendAreaChart timeline={timeline} />
                </SliceBody>
              </ModernTabPanel>
            </Col>
            <Col xs={24} lg={10}>
              <ModernTabPanel
                title="Eureka return status"
                description="How returns are classified in Eureka for this period."
                icon={<BarChartOutlined />}
                iconVariant="amber"
                variant="subtle"
              >
                <SliceBody error={errors.breakdown} label="return statuses">
                  <EurekaStatusDonut rows={breakdown?.byEurekaStatus ?? []} />
                </SliceBody>
              </ModernTabPanel>
            </Col>
          </Row>
        ),
      },
      {
        key: 'breakdown',
        label: (
          <span>
            <PartitionOutlined /> Breakdown
          </span>
        ),
        children: (
          <div className="dashboard-analytics-page__stacked">
            <SliceError error={errors.breakdown} label="breakdown" />
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ModernTabPanel
                  title="Top enterprises — orders"
                  description="Highest order volume by Eureka enterprise id."
                  icon={<ShopOutlined />}
                  iconVariant="blue"
                  variant="subtle"
                >
                  <EnterpriseBarChart
                    rows={breakdown?.byEnterprise ?? []}
                    metric="orders"
                    resolveEnterprise={resolveEnterprise}
                  />
                </ModernTabPanel>
              </Col>
              <Col xs={24} lg={12}>
                <ModernTabPanel
                  title="Top enterprises — returns"
                  description="Highest return (suivi) volume by enterprise."
                  icon={<TeamOutlined />}
                  iconVariant="purple"
                  variant="subtle"
                >
                  <EnterpriseBarChart
                    rows={breakdown?.byEnterprise ?? []}
                    metric="suivi"
                    resolveEnterprise={resolveEnterprise}
                  />
                </ModernTabPanel>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <ModernTabPanel
                  title="Returns by staff profile"
                  description="Who initiated returns (createdByRole)."
                  icon={<UserOutlined />}
                  iconVariant="teal"
                  variant="subtle"
                >
                  <RoleBarChart rows={breakdown?.byRole ?? []} />
                </ModernTabPanel>
              </Col>
              <Col xs={24} lg={12}>
                <ModernTabPanel
                  title="Active enterprises"
                  description="Distinct enterprises with activity in the selected window."
                  icon={<TeamOutlined />}
                  iconVariant="slate"
                  variant="subtle"
                >
                  <Typography.Paragraph className="dashboard-analytics-page__paragraph">
                    Enterprises active in this window:{' '}
                    <Typography.Text strong>
                      {overview?.totals.enterprises.value ?? '—'}
                    </Typography.Text>
                  </Typography.Paragraph>
                  <Divider className="dashboard-analytics-page__divider" />
                  <Typography.Text
                    type="secondary"
                    className="dashboard-analytics-page__hint"
                  >
                    Charts above group volumes by Eureka enterprise id (numeric).
                  </Typography.Text>
                </ModernTabPanel>
              </Col>
            </Row>
          </div>
        ),
      },
      {
        key: 'products',
        label: (
          <span>
            <ShoppingOutlined /> Products
          </span>
        ),
        children: (
          <ModernTabPanel
            title="Product signals"
            description="Most ordered line items and most frequent return line items."
            icon={<ShoppingOutlined />}
            iconVariant="green"
            variant="subtle"
          >
            <SliceBody error={errors.products} label="product signals">
              <TopProductsTable products={products} loading={loading.products} />
            </SliceBody>
          </ModernTabPanel>
        ),
      },
      {
        key: 'activity',
        label: (
          <span>
            <HistoryOutlined /> Recent activity
          </span>
        ),
        children: (
          <ModernTabPanel
            title="Latest orders & returns"
            description="Most recently created records in the selected period."
            icon={<HistoryOutlined />}
            iconVariant="purple"
            variant="subtle"
          >
            <SliceError error={ordersQuery.error} label="orders" />
            <SliceError error={returnsQuery.error} label="returns" />
            <RecentActivity
              orders={recentOrders}
              returns={recentReturns}
              resolveEnterprise={resolveEnterprise}
            />
          </ModernTabPanel>
        ),
      },
      {
        key: 'orders-list',
        label: (
          <span>
            <OrderedListOutlined /> All orders
          </span>
        ),
        children: targetOrgId ? (
          <Listing
            title="All orders"
            subTitle="Full paginated list of orders in the selected period."
            org={_org ?? ''}
            idKey="orderId"
            schema={createDashboardOrderSchema(resolveEnterprise)}
            options={listingOpts as any}
            fetchHook={useDashboardOrders as any}
            hasNew={false}
            hasEdit={false}
            hasView={false}
            hasDelete={false}
            hasRefresh={false}
            enableSelection={false}
          />
        ) : (
          <Empty description="Pick a client organization to load orders." />
        ),
      },
      {
        key: 'returns-list',
        label: (
          <span>
            <SwapOutlined /> All returns
          </span>
        ),
        children: targetOrgId ? (
          <Listing
            title="All returns (suivi)"
            subTitle="Full paginated list of return records in the selected period."
            org={_org ?? ''}
            idKey="suiviId"
            schema={createDashboardSuiviSchema(resolveEnterprise)}
            options={listingOpts as any}
            fetchHook={useDashboardReturns as any}
            hasNew={false}
            hasEdit={false}
            hasView={false}
            hasDelete={false}
            hasRefresh={false}
            enableSelection={false}
          />
        ) : (
          <Empty description="Pick a client organization to load returns." />
        ),
      },
    ],
    [
      _org,
      listingOpts,
      timeline,
      breakdown,
      products,
      recentOrders,
      recentReturns,
      resolveEnterprise,
      loading.products,
      overview?.totals.enterprises.value,
      errors.timeline,
      errors.breakdown,
      errors.products,
    ],
  );

  return (
    <div className="dashboard-analytics-page">
      <ModernTabPanel
        className="dashboard-analytics-page__hero-panel"
        title="Analytics dashboard"
        description="Operational snapshot for one client organization — switch tenant and period above."
        icon={<BarChartOutlined />}
        iconVariant="blue"
        actions={toolbar}
      >
        {periodHint ? (
          <Typography.Text
            type="secondary"
            className="dashboard-analytics-page__window-hint"
          >
            Selected window: <strong>{periodHint}</strong>
          </Typography.Text>
        ) : null}

        {!targetOrgId ? (
          <Empty
            className="dashboard-analytics-page__empty"
            description="Pick a client organization to load analytics."
          />
        ) : null}

        <SliceError error={errors.overview} label="overview totals" />

        <Row
          gutter={[16, 16]}
          align="stretch"
          className="dashboard-analytics-page__kpi-row"
        >
          <Col xs={24} sm={12} lg={6}>
            <KpiCard
              title="Orders"
              value={overview?.totals.orders.value ?? 0}
              delta={overview?.totals.orders.delta ?? null}
              deltaTone="growth-good"
              loading={loading.overview}
              icon={<ShopOutlined />}
              accent="#1677ff"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <KpiCard
              title="Returns (suivi)"
              value={overview?.totals.suivi.value ?? 0}
              delta={overview?.totals.suivi.delta ?? null}
              deltaTone="growth-bad"
              loading={loading.overview}
              icon={<BarChartOutlined />}
              accent="#fa8c16"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <KpiCard
              title="Return rate"
              value={overview?.totals.returnRate.value ?? 0}
              suffix="%"
              precision={1}
              delta={overview?.totals.returnRate.delta ?? null}
              deltaSuffix=" pts"
              deltaTone="growth-bad"
              loading={loading.overview}
              accent="#eb2f96"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <KpiCard
              title="Unique clients"
              value={overview?.totals.uniqueClients.value ?? 0}
              showDelta={false}
              loading={loading.overview}
              icon={<UserOutlined />}
              accent="#13c2c2"
            />
          </Col>
        </Row>
      </ModernTabPanel>

      <div className="dashboard-analytics-page__tabs-wrap">
        <ModernTabs variant="elevated" defaultActiveKey="overview" items={tabItems} />
      </div>
    </div>
  );
};

const Dashboard = (props: PageProps) => (
  <FeaturePermission feature="menus:dashboard" title="Analytics dashboard">
    <DashboardInner {...props} />
  </FeaturePermission>
);

export default Dashboard;
